import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import {
  QuizApi,
  type CreateUpdateQuizWithQuestionsRequest,
} from "@/api/giftcard.api";

export type CreatedQuizTableRow = {
  id: string;
  /** Server quiz id for public link `/quiz?quizId=` */
  quizId?: number;
  title: string;
  description: string;
  timeLimitSeconds: number;
  maxScore: number;
  passingScore: number;
  /** Present when row comes from GetAllQuizzes */
  isActive?: boolean;
  createdBy?: number;
  createdDatetime?: string;
  questions: {
    id: string;
    text: string;
    difficulty: string;
    questionType: string;
    options: { id: string; text: string; isCorrect: boolean }[];
  }[];
};

type Difficulty = "Easy" | "Medium" | "Hard";

type OptionDraft = { text: string };

type QuestionDraft = {
  question_text: string;
  difficulty: Difficulty;
  options: OptionDraft[];
  correctOptionIndex: number;
};

const defaultQuestion = (): QuestionDraft => ({
  question_text: "",
  difficulty: "Easy",
  options: [
    { text: "" },
    { text: "" },
    { text: "" },
    { text: "" },
  ],
  correctOptionIndex: 0,
});

export function CreateQuizForm({
  onSuccess,
  onCancel,
  quizId = 0,
}: {
  onSuccess: (row: CreatedQuizTableRow) => void;
  onCancel: () => void;
  /** Existing quiz id for update; 0 creates a new quiz */
  quizId?: number;
}) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(5);
  const [maxScore, setMaxScore] = useState(100);
  const [passingScore, setPassingScore] = useState(35);
  const [createdBy, setCreatedBy] = useState(1);
  const [questions, setQuestions] = useState<QuestionDraft[]>([defaultQuestion()]);
  const [submitting, setSubmitting] = useState(false);

  const updateQuestion = (idx: number, patch: Partial<QuestionDraft>) => {
    setQuestions((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const addQuestion = () => setQuestions((prev) => [...prev, defaultQuestion()]);

  const removeQuestion = (idx: number) => {
    setQuestions((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const updateOption = (qIdx: number, optIdx: number, text: string) => {
    setQuestions((prev) => {
      const next = [...prev];
      const opts = [...next[qIdx].options];
      opts[optIdx] = { text };
      next[qIdx] = { ...next[qIdx], options: opts };
      return next;
    });
  };

  const addOption = (qIdx: number) => {
    setQuestions((prev) => {
      const next = [...prev];
      const q = next[qIdx];
      next[qIdx] = {
        ...q,
        options: [...q.options, { text: "" }],
        correctOptionIndex:
          q.correctOptionIndex >= q.options.length ? q.options.length : q.correctOptionIndex,
      };
      return next;
    });
  };

  const removeOption = (qIdx: number, optIdx: number) => {
    setQuestions((prev) => {
      const next = [...prev];
      const q = next[qIdx];
      if (q.options.length <= 2) return prev;
      const opts = q.options.filter((_, i) => i !== optIdx);
      let correct = q.correctOptionIndex;
      if (optIdx === correct) correct = 0;
      else if (optIdx < correct) correct -= 1;
      next[qIdx] = { ...q, options: opts, correctOptionIndex: correct };
      return next;
    });
  };

  const buildPayload = (): CreateUpdateQuizWithQuestionsRequest => {
    const p_TimeLimitSeconds = Math.max(1, Math.round(timeLimitMinutes * 60));
    return {
      quizDetails: {
        p_Id: quizId,
        p_Name: name.trim(),
        p_Description: description.trim(),
        p_TimeLimitSeconds,
        p_MaxScore: maxScore,
        p_PassingScore: passingScore,
        p_CreatedBy: createdBy,
        p_UpdatedBy: quizId ? createdBy : 0,
      },
      listQuestions: questions.map((q, qi) => {
        const withText = q.options
          .map((o, origIndex) => ({ text: o.text.trim(), origIndex }))
          .filter((x) => x.text.length > 0);
        return {
          question_text: q.question_text.trim(),
          question_type: "MCQ",
          difficulty: q.difficulty,
          order_index: qi + 1,
          options: withText.map((o, j) => ({
            text: o.text,
            is_correct: (o.origIndex === q.correctOptionIndex ? 1 : 0) as 0 | 1,
            order_index: j + 1,
          })),
        };
      }),
    };
  };

  const validate = (): string | null => {
    if (!name.trim()) return "Quiz name is required.";
    if (timeLimitMinutes <= 0) return "Time limit must be greater than zero.";
    if (maxScore <= 0) return "Max score must be greater than zero.";
    if (passingScore < 0 || passingScore > maxScore) {
      return "Passing score must be between 0 and max score.";
    }
    if (!questions.length) return "Add at least one question.";
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question_text.trim()) return `Question ${i + 1}: text is required.`;
      const filled = q.options.filter((o) => o.text.trim());
      if (filled.length < 2) return `Question ${i + 1}: enter at least two options with text.`;
      if (q.correctOptionIndex < 0 || q.correctOptionIndex >= q.options.length) {
        return `Question ${i + 1}: select the correct answer.`;
      }
      if (!q.options[q.correctOptionIndex]?.text.trim()) {
        return `Question ${i + 1}: the selected correct option must have text.`;
      }
    }
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) {
      toast({ title: "Validation", description: err, variant: "destructive" });
      return;
    }
    const payload = buildPayload();
    setSubmitting(true);
    try {
      const response = await QuizApi.createUpdateQuizWithQuestions(payload);
      if (response.error_msg && String(response.error_msg).trim() !== "") {
        toast({
          title: "Error",
          description: String(response.error_msg),
          variant: "destructive",
        });
        return;
      }
      const returnedId =
        (response.quizDetails as { p_Id?: number } | undefined)?.p_Id ?? quizId;
      const id =
        returnedId && returnedId > 0
          ? `QZ-${returnedId}`
          : `QZ-${Date.now()}`;

      const row: CreatedQuizTableRow = {
        id,
        quizId: returnedId && returnedId > 0 ? returnedId : undefined,
        title: payload.quizDetails.p_Name,
        description: payload.quizDetails.p_Description,
        timeLimitSeconds: payload.quizDetails.p_TimeLimitSeconds,
        maxScore: payload.quizDetails.p_MaxScore,
        passingScore: payload.quizDetails.p_PassingScore,
        questions: questions.map((q, qi) => {
          const opts = q.options
            .map((o, oi) => ({ text: o.text.trim(), oi }))
            .filter((x) => x.text.length > 0);
          return {
            id: `q${qi + 1}`,
            text: q.question_text.trim(),
            difficulty: q.difficulty,
            questionType: "MCQ",
            options: opts.map((x, j) => ({
              id: `q${qi + 1}-o${j + 1}`,
              text: x.text,
              isCorrect: x.oi === q.correctOptionIndex,
            })),
          };
        }),
      };

      toast({
        title: "Success",
        description: "Quiz saved successfully.",
      });
      onSuccess(row);
    } catch (error) {
      console.error("CreateUpdateQuizWithQuestions:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to save quiz",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Quiz name</Label>
          <Input
            placeholder="e.g. BO Games"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>Time limit (minutes)</Label>
          <Input
            type="number"
            min={1}
            step={1}
            value={timeLimitMinutes}
            onChange={(e) => setTimeLimitMinutes(parseInt(e.target.value || "1", 10) || 1)}
          />
          <p className="text-xs text-muted-foreground">
            Sent to API as seconds ({Math.max(1, Math.round(timeLimitMinutes * 60))} s).
          </p>
        </div>
        <div className="md:col-span-2 space-y-2">
          <Label>Description</Label>
          <Input
            placeholder="Short description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label>Max score</Label>
          <Input
            type="number"
            min={1}
            value={maxScore}
            onChange={(e) => setMaxScore(parseInt(e.target.value || "1", 10) || 1)}
          />
        </div>
        <div className="space-y-2">
          <Label>Passing score</Label>
          <Input
            type="number"
            min={0}
            value={passingScore}
            onChange={(e) => setPassingScore(parseInt(e.target.value || "0", 10) || 0)}
          />
        </div>
        <div className="space-y-2">
          <Label>Created by (user id)</Label>
          <Input
            type="number"
            min={1}
            value={createdBy}
            onChange={(e) => setCreatedBy(parseInt(e.target.value || "1", 10) || 1)}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Questions (MCQ)</h3>
          <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
            + Add question
          </Button>
        </div>

        {questions.map((q, qIdx) => (
          <div
            key={qIdx}
            className="rounded-lg border border-border p-4 space-y-4 bg-muted/30"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 space-y-2">
                <Label>{`Question ${qIdx + 1}`}</Label>
                <Input
                  placeholder="Question text"
                  value={q.question_text}
                  onChange={(e) => updateQuestion(qIdx, { question_text: e.target.value })}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={() => removeQuestion(qIdx)}
                title="Remove question"
                disabled={questions.length <= 1}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>

            <div className="space-y-2 max-w-xs">
              <Label>Difficulty</Label>
              <Select
                value={q.difficulty}
                onValueChange={(v: Difficulty) => updateQuestion(qIdx, { difficulty: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Easy">Easy</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Options — mark the correct answer</Label>
                <Button type="button" variant="ghost" size="sm" onClick={() => addOption(qIdx)}>
                  + Option
                </Button>
              </div>
              <RadioGroup
                value={String(q.correctOptionIndex)}
                onValueChange={(v) =>
                  updateQuestion(qIdx, { correctOptionIndex: parseInt(v, 10) })
                }
                className="space-y-2"
              >
                {q.options.map((opt, oIdx) => (
                  <div key={oIdx} className="flex items-center gap-2">
                    <RadioGroupItem value={String(oIdx)} id={`q${qIdx}-o${oIdx}`} />
                    <Input
                      className="flex-1"
                      placeholder={`Option ${oIdx + 1}`}
                      value={opt.text}
                      onChange={(e) => updateOption(qIdx, oIdx, e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={q.options.length <= 2}
                      onClick={() => removeOption(qIdx, oIdx)}
                      title="Remove option"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={submitting || !name.trim()}
          className="bg-orange-600 text-white hover:bg-orange-700"
        >
          {submitting ? "Saving…" : "Save quiz"}
        </Button>
      </div>
    </div>
  );
}
